import { useMemo } from 'react'
import { createQueryOptions } from '@connectrpc/connect-query'
import { useQuery as useTanStackQuery } from '@tanstack/react-query'
import { create } from '@bufbuild/protobuf'
import { useStore } from '@tanstack/react-store'
import { startOfDay, startOfWeek, endOfWeek, addDays, subDays, addYears } from 'date-fns'
import type { Transport } from '@connectrpc/connect'

import { getDashboardSummary } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { GetDashboardSummaryRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import { InquiryStageEnum, TaskStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { dateToTimestamp, dateToDateOnlyTimestamp } from '@/lib/date-utils'
import { connectTransport } from '@/lib/connect-transport'
import { organizationStore } from '@/stores/organization.store'
import { useCurrentUser } from '@/stores/user.store'
import { useLeadsRecencyDays, useUpcomingPaymentsDaysAhead } from '@/stores/dashboard-settings.store'

/**
 * Shared query options for the GetDashboardSummary aggregate RPC, which
 * replaces the ~9 per-widget RPCs the dashboard used to fire. All dashboard
 * cards read from this ONE query and derive their own slice client-side:
 * - OperationsCards: counts derived from tasksTotalCount/openLeadsCount/
 *   overduePaymentSchedulesTotalCount and a client-side filter of `events`.
 * - ThisWeekCard: renders `events` directly (already scoped to weekStart/weekEnd).
 * - TaskCard: filters the wide `tasks` array (30-day window) down to its
 *   selected 7/14/30-day window client-side.
 * - LeadCard: renders `recentInquiries` directly, split by stage client-side.
 * - PaymentScheduleCard: renders `overduePaymentSchedules`/`upcomingPaymentSchedules`.
 *
 * Cache stability: every widget calling {@link useDashboardSummary} with the
 * SAME (default) parameters produces a byte-identical request, so TanStack
 * Query dedupes them into a single network call on first load. Timestamps are
 * normalized to day boundaries (via `startOfDay`) so recomputing them a few ms
 * apart across renders still yields identical values and doesn't fragment the
 * cache. Widgets with an active, non-default selector (lead recency, upcoming
 * payment window, or the ThisWeekCard week offset) diverge onto their own
 * cache entry, matching how those selectors already triggered independent
 * fetches before this RPC was introduced.
 */

/** Days ahead the dashboard pre-fetches pending tasks for (the widest task window). */
const DASHBOARD_TASK_WINDOW_DAYS = 30

/** Max overdue payment schedules fetched for the dashboard (shared cache entry). */
const DASHBOARD_OVERDUE_SCHEDULE_LIMIT = 100

/** Max upcoming payment schedules fetched for the dashboard. */
const DASHBOARD_UPCOMING_SCHEDULE_LIMIT = 20

/** Max recent leads fetched for LeadCard. */
const DASHBOARD_RECENT_LEADS_LIMIT = 10

/** Default lead recency window (days), matching dashboard-settings.store's default. */
const DEFAULT_LEADS_RECENCY_DAYS = 7

/** Default upcoming-payments lookahead (days), matching dashboard-settings.store's default. */
const DEFAULT_UPCOMING_PAYMENTS_DAYS_AHEAD = 7

const RECENT_LEAD_STAGES = [
  InquiryStageEnum.INQUIRY_STAGE_NEW,
  InquiryStageEnum.INQUIRY_STAGE_IN_PROGRESS,
]

const OPEN_LEAD_STAGES = [
  InquiryStageEnum.INQUIRY_STAGE_NEW,
  InquiryStageEnum.INQUIRY_STAGE_IN_PROGRESS,
  InquiryStageEnum.INQUIRY_STAGE_QUOTE_SENT,
]

const PENDING_TASK_STATUSES = [
  TaskStatusEnum.TASK_STATUS_NOT_STARTED,
  TaskStatusEnum.TASK_STATUS_IN_PROGRESS,
]

/**
 * Monday–Sunday window for the given week offset (0 = current week).
 * Consumed by ThisWeekCard (renders the whole week) and OperationsCards
 * (derives its "today onward" count by filtering client-side).
 */
function getDashboardWeekWindow(weekOffset = 0) {
  const base = addDays(new Date(), weekOffset * 7)
  return {
    weekStart: startOfWeek(base, { weekStartsOn: 1 }),
    weekEnd: endOfWeek(base, { weekStartsOn: 1 }),
  }
}

/**
 * End of the pending-task due-date window (start-of-today + 30 days). No start
 * bound is sent, so overdue tasks are included too — OperationsCards' count
 * and TaskCard's client-side filter both rely on that. TaskCard narrows this
 * wide response down to its own selected 7/14/30-day window locally.
 */
function getDashboardTaskDueWindowEnd() {
  return addDays(startOfDay(new Date()), DASHBOARD_TASK_WINDOW_DAYS)
}

export interface DashboardSummaryParams {
  organizationId: string
  /** Required for the tasks slice to scope to "my tasks" — omit to disable the query. */
  currentUserId?: string
  /** Week offset for the events slice (0 = current week). Only ThisWeekCard's navigation uses non-zero. */
  weekOffset?: number
  /** Recency window (days) for the recent-leads slice. Defaults to 7, mirroring dashboard-settings.store. */
  leadsRecencyDays?: number
  /** Lookahead window (days) for the upcoming-payments slice. Defaults to 7, mirroring dashboard-settings.store. */
  upcomingPaymentsDaysAhead?: number
}

export function createDashboardSummaryRequest(params: DashboardSummaryParams) {
  const { weekStart, weekEnd } = getDashboardWeekWindow(params.weekOffset ?? 0)
  const todayStart = startOfDay(new Date())
  const recencyDays = params.leadsRecencyDays ?? DEFAULT_LEADS_RECENCY_DAYS
  const upcomingDaysAhead = params.upcomingPaymentsDaysAhead ?? DEFAULT_UPCOMING_PAYMENTS_DAYS_AHEAD

  return create(GetDashboardSummaryRequestSchema, {
    organizationId: params.organizationId,
    weekStart: dateToTimestamp(weekStart),
    weekEnd: dateToTimestamp(weekEnd),
    taskAssignedTo: params.currentUserId,
    taskStatuses: PENDING_TASK_STATUSES,
    taskDueDateEnd: dateToDateOnlyTimestamp(getDashboardTaskDueWindowEnd()),
    leadStages: RECENT_LEAD_STAGES,
    leadSince: dateToTimestamp(subDays(todayStart, recencyDays)),
    leadEventDateStart: dateToTimestamp(subDays(todayStart, 2)),
    leadEventDateEnd: dateToTimestamp(addYears(todayStart, 2)),
    leadLimit: DASHBOARD_RECENT_LEADS_LIMIT,
    openLeadStages: OPEN_LEAD_STAGES,
    overdueScheduleLimit: DASHBOARD_OVERDUE_SCHEDULE_LIMIT,
    upcomingScheduleDaysAhead: upcomingDaysAhead,
    upcomingScheduleLimit: DASHBOARD_UPCOMING_SCHEDULE_LIMIT,
  })
}

/**
 * Query options for the GetDashboardSummary RPC, for use in route loaders via
 * queryClient.ensureQueryData/prefetchQuery. Uses the same connectTransport
 * singleton as {@link useDashboardSummary} so the cache key matches.
 */
export function dashboardSummaryQueryOptions(
  params: DashboardSummaryParams,
  transport: Transport = connectTransport,
) {
  return createQueryOptions(getDashboardSummary, createDashboardSummaryRequest(params), { transport })
}

/**
 * Hook for the dashboard's single aggregate query. Every dashboard widget
 * calls this (optionally overriding `weekOffset` for ThisWeekCard's
 * navigation) and derives its own slice of the response client-side.
 */
export function useDashboardSummary(options?: { weekOffset?: number }) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)
  const { user: currentUser } = useCurrentUser()
  const leadsRecencyDays = useLeadsRecencyDays()
  const upcomingPaymentsDaysAhead = useUpcomingPaymentsDaysAhead()
  const weekOffset = options?.weekOffset ?? 0

  const params = useMemo<DashboardSummaryParams>(() => ({
    organizationId: selectedOrganizationId || '',
    currentUserId: currentUser?.id,
    weekOffset,
    leadsRecencyDays,
    upcomingPaymentsDaysAhead,
  }), [selectedOrganizationId, currentUser?.id, weekOffset, leadsRecencyDays, upcomingPaymentsDaysAhead])

  const queryOptions = useMemo(() => dashboardSummaryQueryOptions(params), [params])

  const query = useTanStackQuery({
    ...queryOptions,
    enabled: !!selectedOrganizationId && !!currentUser?.id,
    staleTime: 2 * 60 * 1000, // 2 minutes - includes payment schedule data, which can change frequently
  })

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    hasLoaded: query.isSuccess || !!query.data,
  }
}
